import React, {useEffect, useRef, useState} from 'react';
import {useStores} from "../../../stores/Context";
import {createAxiosEntrepriseInstance} from "../../../axiosEntreprise";
import PaletteCouleurs from "../../PaletteCouleurs";
import CorbeilleSvg from "../../../boiteAOutils/SVG/CorbeilleSvg";
import QuitterSvg from "../../../boiteAOutils/SVG/QuitterSvg";
import CroixSvg from "../../../boiteAOutils/SVG/CroixSvg";
import FlecheRevenirSvg from "../../../boiteAOutils/SVG/FlecheRevenirSvg";
import {useParams} from "react-router-dom";
import BtnAction from "../../../boiteAOutils/BtnAction";
import BtnPrimary from "../../../boiteAOutils/BtnPrimary";
import BtnPlus from "../../../boiteAOutils/BtnPlus";
import BtnSvg from "../../../boiteAOutils/BtnSvg";
import BtnTiertary from "../../../boiteAOutils/BtnTiertary";
import BtnSecondary from "../../../boiteAOutils/BtnSecondary";

const WidgetBibliothequeImagesCamera = (props) => {
    const {widgetStore, bibliothequeImagesStore} = useStores();
    const {id, ent} = useParams();

    const videoRef = useRef(null);
    const annotationRef = useRef(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [ctx, setCtx] = useState(null);
    const [isCameraActive, setIsCameraActive] = useState(true);
    const [tool, setTool] = useState("draw");
    const [color, setColor] = useState("#00b0ff");
    const [lineWidth, setLineWidth] = useState(4);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const api = createAxiosEntrepriseInstance();

    // Démarrer la caméra
    useEffect(() => {
        let stream;
        if (isCameraActive) {
            navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
                .then(currentStream => {
                    stream = currentStream;
                    if (videoRef.current) videoRef.current.srcObject = currentStream;
                })
                .catch(() => setError("La caméra n’est pas accessible. Vérifiez l’autorisation puis réessayez."));
        }
        return () => stream?.getTracks().forEach(track => track.stop());
    }, [isCameraActive]);

    // Adapter le canvas à l'image affichée
    useEffect(() => {
        if (annotationRef.current && capturedImage) {
            const img = document.querySelector("#captured-img");
            if (img) {
                const rect = img.getBoundingClientRect();
                annotationRef.current.width = rect.width;
                annotationRef.current.height = rect.height;
                const annotationCtx = annotationRef.current.getContext("2d");
                annotationCtx.lineCap = "round";
                setCtx(annotationCtx);
            }
        }
    }, [capturedImage]);

    // Capturer l'image depuis la vidéo
    const capturePhoto = () => {
        const video = videoRef.current;
        if (video && video.videoWidth && video.videoHeight) {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const context = canvas.getContext("2d");
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            context.imageSmoothingEnabled = false;
            setCapturedImage(canvas.toDataURL("image/png"));
            setIsCameraActive(false); // Fermer la caméra
        } else {
            setError("La caméra se prépare encore. Patientez une seconde puis recommencez.");
        }
    };

    // Récupérer coordonnées (souris ou tactile)
    const getEventPosition = (event) => {
        const rect = annotationRef.current.getBoundingClientRect();
        let clientX, clientY;
        if (event.touches && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    // Début du dessin
    const startDrawing = (event) => {
        if (!ctx) return;
        const { x, y } = getEventPosition(event);
        ctx.strokeStyle = tool === "erase" ? "white" : color;
        ctx.lineWidth = tool === "erase" ? 20 : lineWidth;
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    // Dessiner
    const draw = (event) => {
        if (!isDrawing || !ctx) return;
        const { x, y } = getEventPosition(event);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    // Fin du dessin
    const stopDrawing = () => setIsDrawing(false);

    // Effacer tout
    const clearCanvas = () => {
        if (annotationRef.current) {
            const ctx = annotationRef.current.getContext("2d");
            ctx.clearRect(0, 0, annotationRef.current.width, annotationRef.current.height);
        }
    };

    // Télécharger l'image annotée
    const downloadPhoto = async () => {
        if (!annotationRef.current || !capturedImage) return;
        setError('');
        const img = document.querySelector("#captured-img");
        const rect = img.getBoundingClientRect();

        const finalCanvas = document.createElement("canvas");
        finalCanvas.width = rect.width;
        finalCanvas.height = rect.height;
        const finalCtx = finalCanvas.getContext("2d");

        const image = new Image();
        image.src = capturedImage;
        await new Promise(resolve => (image.onload = resolve));
        finalCtx.drawImage(image, 0, 0, rect.width, rect.height);

        finalCtx.drawImage(annotationRef.current, 0, 0);

        finalCanvas.toBlob(async (blob) => {
            if (!blob) {
                setError("La capture n’a pas pu être générée. Recommencez la photo.");
                return;
            }
            try {
                setIsSaving(true);
                const formData = new FormData();
                formData.append("file", blob, "annotated_photo.png");
                formData.append("id_totem", widgetStore.idTotem);

                await api.post('/api/bibliothequeImages/upload', formData);

                props.onAddPhotos();
                bibliothequeImagesStore.handleCloseCamera();
            } catch (requestError) {
                setError(requestError.response?.data?.message || "La capture n’a pas pu être enregistrée.");
            } finally {
                setIsSaving(false);
            }
        }, "image/png");
    };

    return (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black" style={{ zIndex: 100000 }}>
            {error && <div className="alert alert-danger position-absolute top-0 m-3" role="alert" style={{ zIndex: 100001 }}>{error}</div>}
            {isCameraActive ? (
                <>
                    <video ref={videoRef} autoPlay className="absolute inset-0 w-full h-full object-cover" />
                    <div
                        onClick={capturePhoto}
                        className="absolute bottom-10 bg-white text-black px-6 py-3 rounded-full"
                        style={{ width: '75px', height: '75px', border: '5px solid #707070', backgroundColor: "#eeeeee" }}
                    />
                </>
            ) : (
                <div className="relative w-[80%] h-[80%] bg-white flex items-center justify-center rounded-lg shadow-lg">
                    <img
                        id="captured-img"
                        src={capturedImage}
                        alt="Captured"
                        className="absolute w-full h-full object-contain rounded-lg"
                    />

                    <canvas
                        ref={annotationRef}
                        className="absolute"
                        style={{ touchAction: "none" }}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        onTouchCancel={stopDrawing}
                    />

                    {/* Barre d'outils */}
                    <div
                        className="absolute bottom-0 left-0 right-0 flex justify-content-around align-items-center gap-4 p-3 rounded-b-lg"
                        style={{ backgroundColor: "#f1f5f7" }}
                    >
                        <div className={"btnAnnotationCameraContent"}>
                            <div className={"btnAnnotationCamera"}>
                                <PaletteCouleurs color={color} onColorChange={val => setColor(val)} />
                                <span>Annoter</span>
                            </div>
                            <div className={"btnAnnotationCamera"} onClick={clearCanvas}>
                                <CorbeilleSvg height={35} width={35} />
                                <span>Effacer</span>
                            </div>
                        </div>

                        <BtnPrimary onClick={downloadPhoto} disabled={isSaving} className="bg-green-500 text-white px-6 py-3 rounded-full">
                            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
                        </BtnPrimary>
                        <BtnSecondary onClick={() => setIsCameraActive(true)} className="bg-blue-500 text-white px-6 py-3 rounded-full">
                            Recommencer
                        </BtnSecondary>
                        <BtnTiertary onClick={() => bibliothequeImagesStore.handleCloseCamera()} >
                            Annuler
                        </BtnTiertary>
                    </div>
                </div>
            )}
        </div>
    );
};


export default WidgetBibliothequeImagesCamera;
